import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import supabase from '../lib/supabaseClient'
import type { AuthChangeEvent, Session } from '@supabase/supabase-js'

interface AuthContextValue {
   isLoading: boolean
   isAuthenticated: boolean
   email: string | null
   signInWithPassword: (email: string, password: string) => Promise<{ error?: string }>
   signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
   const client = supabase

   const [isLoading, setIsLoading] = useState(true)
   const [email, setEmail] = useState<string | null>(null)

   useEffect(() => {
      const init = async () => {
         const { data } = await client.auth.getSession()
         setEmail(data.session?.user?.email ?? null)
         setIsLoading(false)
      }
      init()

      const { data: listener } = client.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
         setEmail(session?.user?.email ?? null)
      })
      return () => {
         listener.subscription.unsubscribe()
      }
   }, [client])

   const signInWithPassword: AuthContextValue['signInWithPassword'] = async (email, password) => {
      const { error } = await client.auth.signInWithPassword({ email, password })
      if (error) return { error: error.message }
      return {}
   }

   const signOut = async () => {
      await client.auth.signOut()
   }

   const value = useMemo<AuthContextValue>(
      () => ({ isLoading, isAuthenticated: !!email, email, signInWithPassword, signOut }),
      [isLoading, email]
   )

   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
   const ctx = useContext(AuthContext)
   if (!ctx) throw new Error('useAuth must be used within AuthProvider')
   return ctx
}


